import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  Box,
  Grid,
  Group,
  Text,
  Button,
  Loader,
  Alert,
  Stack,
  Paper,
  Badge,
  Table,
  Checkbox,
  ScrollArea,
  Modal,
  TextInput,
  Anchor,
  Progress,
  Tooltip,
  ActionIcon,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconCheck,
  IconArrowLeft,
  IconArrowRight,
  IconAlertTriangle,
  IconBarcode,
  IconPrinter,
  IconChevronDown,
  IconLockOpen,
  IconEdit,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useDisclosure } from "@mantine/hooks";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  printSelectedSlide,
  selectedPrintError,
} from "../../services/selectedMaterialPrint.service";
import { useAuth } from "../../hooks/useAuth";
import { InsufficientTissueConfirmationModal } from "../../components/modals/InsufficientTissueConfirmationModal";
import { useHasPermission } from "../../hooks/useHasPermission";
import {
  useCaseForCutting,
  useStartCutting,
  useCaseLock,
  useReleaseLock,
  useExtendLock,
  useForceReleaseLock,
  useProcessCassetteScan,
  useReprintSlides,
  useCompleteCutting,
  useCuttingQueueContext,
  useOpenCaseByBarcode,
} from "../../hooks/useWorkflows";
import {
  GrossingCaseSummary,
  GrossDescriptionEditorModal,
  GrossingRequisitionCard,
} from "../../components/grossing";
import { CaseNotesSection } from "../../components/accessioning/CaseNotesSection";
import { CaseFlagsSection } from "../../components/cases/CaseFlagsSection";
import { CaseCommentsSection } from "../../components/accessioning/CaseCommentsSection";
import ErrorsCard from "../../components/accessioning/ErrorsCard";
import {
  slideLabelPrinterService,
  type SlideLabelData,
} from "../../services/slideLabelPrinter.service";
import {
  isCompleteCassetteBarcode,
  useBarcodeScanner,
} from "../../hooks/useBarcodeScanner";
import {
  DeleteStainButton,
  SlidePrinterStatusPanel,
} from "../../components/workflows";
import {
  StickerPrinterStatusPanel,
  ReceivedExternalScanInfoPanel,
  ReceivedExternalBadge,
  PrintStickerButton,
} from "../../components/sticker";
import { resolvePatientLabelName } from "../../utils/patientLabelName";
import { shouldShowLegacyScannerControls } from "../../utils/scannerControls";
import {
  getDisplayCaseNumber,
  getPhysicalSlideNumber,
} from "../../utils/caseIdentity";
import { isReceivedExternalSlide } from "../../utils/slideOrigin";
import { requireDateOnlyString } from "../../utils/dateOnly";
import type {
  CuttingCompletionMetadata,
  CuttingCompletionResult,
} from "../../services/workflows.service";

dayjs.extend(relativeTime);

const consumedCuttingScanTransactions = new Set<string>();
const ACTIVE_CUTTING_STATUSES = new Set(["Pending Cutting", "Cutting"]);

function cassetteBelongsToOpenCuttingCase(
  scannedCaseNumber: string,
  caseData: any
): boolean {
  const normalizedScan = scannedCaseNumber.trim().toUpperCase();
  if (normalizedScan === caseData.caseNumber?.toUpperCase()) return true;

  return (
    caseData.accessioningType === "additional_stain" &&
    Boolean(caseData.parentCaseNumber) &&
    normalizedScan === caseData.parentCaseNumber.toUpperCase()
  );
}

/**
 * Convert slide data from API to SlideLabelData format for printing
 */
export function convertToSlideLabelData(
  slides: any[],
  caseData: any,
  specimens: any[]
): SlideLabelData[] {
  return slides.map((slide) => {
    const displayCaseNumber = getDisplayCaseNumber(caseData);
    const physicalSlideNumber = getPhysicalSlideNumber(slide);
    // Find the specimen for this slide
    const specimen = specimens.find((s: any) => s.id === slide.specimenId);
    const specimenLetter = specimen?.number || "?";

    const patientDOB = caseData.patient?.dateOfBirth
      ? requireDateOnlyString(
          caseData.patient.dateOfBirth,
          "Patient date of birth"
        )
      : undefined;

    // Generate barcode in format: {caseNumber};{specimenLetter};{blockNumber};{slideNumber}
    const barcode = `${displayCaseNumber};${specimenLetter};${
      slide.block?.number || "1"
    };${physicalSlideNumber}`;

    const dateReceived = requireDateOnlyString(
      caseData.slideLabelDateReceived ?? caseData.dateReceived,
      "Date received"
    );

    return {
      caseNumber: displayCaseNumber,
      patientName:
        resolvePatientLabelName(caseData.patient, caseData.draftPatientData) ??
        "",
      patientDOB,
      specimenLetter,
      blockNumber: slide.block?.number || "1",
      slideNumber: physicalSlideNumber,
      stainCode: slide.stain?.code || "H&E",
      barcode,
      dateReceived,
    };
  });
}

async function printSlideLabelsOrThrow(
  labels: SlideLabelData[]
): Promise<void> {
  const result = await slideLabelPrinterService.printSlideLabels(labels);
  if (!result.success) {
    throw new Error(result.error || "Failed to print slide labels");
  }
}

export default function CuttingDetailPage() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const showLegacyScannerControls = shouldShowLegacyScannerControls();
  const { user } = useAuth();
  const userId = user?.id;

  // Get scanned block info from URL params
  const initialScannedBlock = searchParams.get("block");

  // State
  const [scannedBlockKey, setScannedBlockKey] = useState<string | null>(
    initialScannedBlock
  );
  const [selectedSlides, setSelectedSlides] = useState<Set<string>>(new Set());
  const [barcodeInput, setBarcodeInput] = useState("");
  const [nextCaseScanInput, setNextCaseScanInput] = useState("");
  const [nextCaseScanError, setNextCaseScanError] = useState<string | null>(
    null
  );
  const [isPrintingSelected, setIsPrintingSelected] = useState(false);
  const [automaticScannerActionInFlight, setAutomaticScannerActionInFlight] =
    useState(false);
  const [
    cancelModalOpened,
    { open: openCancelModal, close: closeCancelModal },
  ] = useDisclosure(false);

  // Gross description editor modal state (mirrors EmbeddingDetailPage)
  const [editingSpecimen, setEditingSpecimen] = useState<any>(null);
  const [
    grossEditorOpened,
    { open: openGrossEditor, close: closeGrossEditor },
  ] = useDisclosure(false);
  const highlightedBlockRef = useRef<HTMLTableRowElement>(null);
  const deferredScannerBarcodeRef = useRef<string | null>(null);
  const automaticScannerActionInFlightRef = useRef(false);
  const activeScannerBarcodeRef = useRef<string | null>(null);
  // Counter to force scroll on every scan (even if same block)
  const [scrollTrigger, setScrollTrigger] = useState(0);
  // Left sidebar scroll indicator state
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [completedCuttingStatus, setCompletedCuttingStatus] = useState<
    { caseId: string; statusName: string } | undefined
  >();
  // Ref for scrolling to errors section
  const errorsRef = useRef<HTMLDivElement>(null);

  const [receivedExternalInfo, setReceivedExternalInfo] = useState<{
    itemLabel: string;
    caseNumber: string;
  } | null>(null);

  const handleDismissReceivedExternalInfo = useCallback(
    () => setReceivedExternalInfo(null),
    []
  );

  // Track if we've already started cutting to prevent infinite loops
  const hasStartedCutting = useRef(false);

  // Reset flags when navigating to a different case
  useEffect(() => {
    hasStartedCutting.current = false;
  }, [caseId]);

  useEffect(() => {
    deferredScannerBarcodeRef.current = null;
    return () => {
      deferredScannerBarcodeRef.current = null;
    };
  }, [caseId]);

  // Queries
  const {
    data: caseData,
    isLoading: caseLoading,
    error: caseError,
    refetch,
  } = useCaseForCutting(caseId);
  const displayCaseNumber = useMemo(
    () => getDisplayCaseNumber(caseData),
    [caseData]
  );
  const cuttingDestinationName = useMemo(() => {
    if (completedCuttingStatus && completedCuttingStatus.caseId === caseId) {
      return completedCuttingStatus.statusName;
    }
    const currentStatusName = caseData?.currentStatus?.name;
    if (
      !caseData?.cuttingCompletedAt ||
      !currentStatusName ||
      ACTIVE_CUTTING_STATUSES.has(currentStatusName)
    ) {
      return undefined;
    }
    return currentStatusName;
  }, [caseData, caseId, completedCuttingStatus]);
  const isCuttingFinalized = Boolean(cuttingDestinationName);

  // Lock query
  const { data: lockData } = useCaseLock(caseId);

  const { data: queueContext } = useCuttingQueueContext(caseId);

  // Mutations
  const startCuttingMutation = useStartCutting();
  const releaseLockMutation = useReleaseLock();
  const extendLockMutation = useExtendLock();
  const processCassetteScanMutation = useProcessCassetteScan();
  const reprintSlidesMutation = useReprintSlides();
  const completeCuttingMutation = useCompleteCutting();
  const openCaseByBarcodeMutation = useOpenCaseByBarcode();

  // Check if locked by another user
  const isLockedByOther =
    lockData && lockData.lockedById && lockData.lockedById !== userId;

  const canForceRelease = useHasPermission("case_locks.force_release");
  const forceReleaseMutation = useForceReleaseLock();

  const handleOpenGrossEditor = useCallback(
    (specimen: any) => {
      setEditingSpecimen(specimen);
      openGrossEditor();
    },
    [openGrossEditor]
  );

  const handleCloseGrossEditor = useCallback(() => {
    closeGrossEditor();
    setEditingSpecimen(null);
  }, [closeGrossEditor]);

  const handleForceUnlock = useCallback(() => {
    if (
      !window.confirm(
        `Force-unlock this case from ${
          lockData?.lockedByName || "another user"
        }? They will lose their lock immediately.`
      )
    )
      return;
    forceReleaseMutation.mutate(caseId!, {
      onSuccess: () => {
        notifications.show({
          title: "Lock Released",
          message: "The case has been unlocked.",
          color: "green",
        });
      },
      onError: (err: Error) => {
        notifications.show({
          title: "Error",
          message: err.message || "Failed to release lock",
          color: "red",
        });
      },
    });
  }, [caseId, lockData, forceReleaseMutation]);

  const previousCaseId = queueContext?.previousCaseId ?? null;
  const nextCaseId = queueContext?.nextCaseId ?? null;
  const hasPreviousCase = previousCaseId !== null;
  const hasNextCase = nextCaseId !== null;
  const hasOtherCasesInQueue = (queueContext?.otherCaseCount ?? 0) > 0;

  const navigateToCase = (targetCaseId: string) => {
    navigate(`/cutting/${targetCaseId}`);
  };

  const recordCuttingCompletion = useCallback(
    (result: CuttingCompletionMetadata) => {
      if (caseId && result.cuttingCompleted && result.currentStatus?.name) {
        setCompletedCuttingStatus({
          caseId,
          statusName: result.currentStatus.name,
        });
      }
    },
    [caseId]
  );

  const handleCompleteCuttingSuccess = useCallback(
    (result: CuttingCompletionResult) => {
      recordCuttingCompletion(result);
      refetch();
      const destinationName = result.currentStatus?.name;
      notifications.show({
        title: "Cutting Complete",
        message: destinationName
          ? `Case advanced to ${destinationName}`
          : "Cutting completed. Refreshing the case status.",
        color: "green",
      });
    },
    [recordCuttingCompletion, refetch]
  );

  const handleCompleteCuttingError = useCallback(() => {
    notifications.show({
      title: "Error",
      message: "Unable to complete cutting. Please try again.",
      color: "red",
    });
  }, []);

  const handleCompleteCutting = useCallback(() => {
    if (!caseId) return;
    completeCuttingMutation.mutate(caseId, {
      onSuccess: handleCompleteCuttingSuccess,
      onError: handleCompleteCuttingError,
    });
  }, [
    caseId,
    completeCuttingMutation,
    handleCompleteCuttingError,
    handleCompleteCuttingSuccess,
  ]);

  const handleStartCuttingError = useCallback(() => {
    hasStartedCutting.current = false;
    notifications.show({
      title: "Error",
      message: "Failed to start cutting",
      color: "red",
    });
    navigate("/cutting");
  }, [navigate]);

  // Auto-start cutting on mount (only if not locked by another user)
  useEffect(() => {
    if (hasStartedCutting.current || initialScannedBlock) return;

    // Blocked cases can still be worked on, only auto-advance is blocked
    if (caseId && userId && caseData && !lockData && !isLockedByOther) {
      hasStartedCutting.current = true;
      startCuttingMutation.mutate(caseId, {
        onError: handleStartCuttingError,
      });
    }
  }, [
    caseData,
    caseId,
    handleStartCuttingError,
    initialScannedBlock,
    isLockedByOther,
    lockData,
    startCuttingMutation,
    userId,
  ]);

  // Extend lock every 5 minutes
  useEffect(() => {
    if (!caseId || !userId || !lockData || isLockedByOther) return;

    const interval = setInterval(() => {
      extendLockMutation.mutate(caseId);
    }, 2 * 60 * 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, userId, lockData, isLockedByOther]);

  // Auto-process cassette scan when opened with scanned block from URL
  // Blocked cases can still print labels, only auto-advance is blocked
  useEffect(() => {
    if (
      !initialScannedBlock ||
      !caseData ||
      !caseId ||
      processCassetteScanMutation.isPending
    ) {
      return;
    }

    // Parse the block key (e.g., "A-1" -> specimenLetter: "A", blockNumber: "1")
    const parts = initialScannedBlock.split("-");
    if (parts.length !== 2) return;

    const [specimenLetter, blockNumber] = parts;

    // Find the block ID
    const matchingSpecimen = (caseData.specimens || []).find(
      (specimen: any) => specimen.number === specimenLetter
    );
    const matchingBlock = matchingSpecimen?.blocks?.find(
      (b: any) => b.number === blockNumber
    );
    const blockId = matchingBlock?.id || null;

    if (blockId) {
      const transactionKey = `${location.key}:${caseId}:${initialScannedBlock}`;
      if (consumedCuttingScanTransactions.has(transactionKey)) return;
      consumedCuttingScanTransactions.add(transactionKey);
      hasStartedCutting.current = true;
      navigate(`/cutting/${caseId}`, { replace: true });
      notifications.show({
        title: "Printing",
        message: "Auto-printing slides for scanned block...",
        color: "blue",
      });

      const processAndPrint = async () => {
        try {
          await startCuttingMutation.mutateAsync(caseId);
        } catch {
          handleStartCuttingError();
          return;
        }

        try {
          const result = await processCassetteScanMutation.mutateAsync({
            caseId,
            blockId,
          });
          recordCuttingCompletion(result);
          if (result.printJob?.count > 0 && result.slides?.length > 0) {
            const labelData = convertToSlideLabelData(
              result.slides,
              caseData,
              caseData.specimens || []
            );
            await printSlideLabelsOrThrow(labelData);
            notifications.show({
              title: "Success",
              message: `Slides for block ${initialScannedBlock} printed successfully.`,
              color: "green",
            });
          } else {
            notifications.show({
              title: "Info",
              message: `Block ${initialScannedBlock} slides already printed.`,
              color: "blue",
            });
          }
          refetch();
        } catch (err: any) {
          notifications.show({
            title: "Error",
            message: err.message || "Failed to auto-print slides",
            color: "red",
          });
        }
      };
      processAndPrint();
    }
  }, [
    initialScannedBlock,
    caseData,
    caseId,
    handleStartCuttingError,
    location.key,
    navigate,
    processCassetteScanMutation,
    recordCuttingCompletion,
    refetch,
    startCuttingMutation,
  ]);

  // Auto-scroll to highlighted block when scanned
  useEffect(() => {
    if (highlightedBlockRef.current && scannedBlockKey) {
      // Small delay to ensure DOM has updated
      setTimeout(() => {
        highlightedBlockRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  }, [scannedBlockKey, scrollTrigger]);

  // Release lock on unmount
  useEffect(() => {
    return () => {
      if (caseId) {
        releaseLockMutation.mutate(caseId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  // Check if left sidebar needs scroll indicator
  useEffect(() => {
    const checkScrollable = () => {
      const scrollContainer = document.querySelector(".left-sidebar-scroll");
      if (scrollContainer) {
        const hasScroll =
          scrollContainer.scrollHeight > scrollContainer.clientHeight;
        const isAtBottom =
          scrollContainer.scrollTop + scrollContainer.clientHeight >=
          scrollContainer.scrollHeight - 10;
        setShowScrollIndicator(hasScroll && !isAtBottom);
      }
    };

    // Check initially and on scroll/resize
    checkScrollable();
    const scrollContainer = document.querySelector(".left-sidebar-scroll");
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", checkScrollable);
    }
    window.addEventListener("resize", checkScrollable);

    // Re-check periodically for content changes
    const interval = setInterval(checkScrollable, 500);

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", checkScrollable);
      }
      window.removeEventListener("resize", checkScrollable);
      clearInterval(interval);
    };
  }, []);

  // Handle barcode scan
  const handleBarcodeSubmit = async () => {
    if (!barcodeInput.trim() || !caseData || !caseId) return;

    try {
      // Parse barcode: caseNumber;specimenLetter;blockNumber
      const parts = barcodeInput.trim().split(";");
      if (parts.length !== 3) {
        throw new Error("Invalid barcode format");
      }

      const [caseNumber, specimenLetter, blockNumber] = parts;

      // Verify case number matches
      if (!cassetteBelongsToOpenCuttingCase(caseNumber, caseData)) {
        throw new Error(
          `Barcode is for case ${caseNumber}, but current case is ${displayCaseNumber}`
        );
      }

      // Find the block
      const targetSpecimen = (caseData.specimens || []).find(
        (specimen: any) => specimen.number === specimenLetter
      );
      const targetBlock = targetSpecimen?.blocks?.find(
        (b: any) => b.number === blockNumber
      );
      const foundBlock = targetBlock
        ? { ...targetBlock, specimenNumber: targetSpecimen.number }
        : null;

      if (!foundBlock) {
        throw new Error(
          `Block ${specimenLetter}-${blockNumber} not found in this case`
        );
      }

      // Check for received-external slides on this block
      const blockSlidesForScan = (caseData?.slides || []).filter(
        (s: any) => s.block?.id === foundBlock.id
      );
      const hasReceivedExternal =
        blockSlidesForScan.length > 0 &&
        blockSlidesForScan.every(isReceivedExternalSlide);
      if (hasReceivedExternal) {
        setReceivedExternalInfo({
          itemLabel: `Block ${specimenLetter}-${blockNumber}`,
          caseNumber: displayCaseNumber,
        });
        setBarcodeInput("");
        return;
      }

      // Process cassette scan (auto-prints slides)
      notifications.show({
        title: "Printing",
        message: "Sending slides to printer...",
        color: "blue",
      });

      const result = await processCassetteScanMutation.mutateAsync({
        caseId,
        blockId: foundBlock.id,
      });
      recordCuttingCompletion(result);

      const blockKey = `${specimenLetter}-${blockNumber}`;

      // Check if auto-advanced
      if (result.autoAdvanced) {
        refetch(); // Refresh to get updated status
      }

      if (result.printJob?.count > 0) {
        // Print slide labels using the printer service
        if (result.slides && result.slides.length > 0 && caseData) {
          const labelData = convertToSlideLabelData(
            result.slides,
            caseData,
            caseData.specimens || []
          );
          await printSlideLabelsOrThrow(labelData);

          notifications.show({
            title: "Success",
            message: `Cassette ${blockKey} scanned. ${result.printJob.count} slide label(s) sent to printer.`,
            color: "green",
          });
        } else {
          notifications.show({
            title: "Success",
            message: `Cassette ${blockKey} scanned. ${result.printJob.count} slide(s) printed.`,
            color: "green",
          });
        }
      } else {
        notifications.show({
          title: "Info",
          message: `Cassette ${blockKey} scanned. Slides already printed.`,
          color: "blue",
        });
      }

      setScannedBlockKey(blockKey);
      setScrollTrigger((prev) => prev + 1); // Force scroll even if same block
      setBarcodeInput("");
      refetch();
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err.message || "Failed to scan barcode",
        color: "red",
      });
      setBarcodeInput("");
    }
  };

  // Handle scan next case (when all blocks printed)
  const handleNextCaseScan = async () => {
    if (!nextCaseScanInput.trim()) return;

    setNextCaseScanError(null);

    const barcode = nextCaseScanInput.trim();
    const parts = barcode.split(";");
    if (parts.length !== 3) {
      setNextCaseScanError(
        "Invalid barcode format. Expected: {caseNumber};{specimenLetter};{blockNumber}"
      );
      setNextCaseScanInput("");
      return;
    }

    const [scannedCaseNumber, specimenLetter, blockNumber] = parts;

    try {
      const caseDataRes = await openCaseByBarcodeMutation.mutateAsync({
        barcode,
        intent: "cutting",
      });
      const statusName = caseDataRes.currentStatus?.name || "";

      if (statusName !== "Pending Cutting" && statusName !== "Cutting") {
        setNextCaseScanError(
          `Case ${scannedCaseNumber} is not in the cutting queue (status: ${
            statusName || "Unknown"
          })`
        );
        setNextCaseScanInput("");
        return;
      }

      navigate(
        `/cutting/${caseDataRes.id}?block=${specimenLetter}-${blockNumber}`
      );
      setNextCaseScanInput("");
      setNextCaseScanError(null);
    } catch (err: any) {
      setNextCaseScanError(err.message || "Failed to open case");
      setNextCaseScanInput("");
    }
  };

  const scannerTotalSlides = caseData?.slides?.length || 0;
  const scannerPrintedSlides =
    caseData?.slides?.filter(
      (slide: any) => slide.printedAt || slide.isCompleted
    ).length || 0;
  const scannerHasBlockingState =
    cancelModalOpened ||
    grossEditorOpened ||
    Boolean(receivedExternalInfo) ||
    isPrintingSelected;
  const scannerHasTransientState =
    processCassetteScanMutation.isPending ||
    reprintSlidesMutation.isPending ||
    completeCuttingMutation.isPending ||
    openCaseByBarcodeMutation.isPending;
  const canScanNextCase =
    scannerTotalSlides > 0 &&
    scannerPrintedSlides === scannerTotalSlides &&
    isCuttingFinalized &&
    hasOtherCasesInQueue &&
    !scannerHasBlockingState &&
    !scannerHasTransientState;

  const processAutomaticBarcodeScan = useCallback(
    async (barcode: string) => {
      const parts = barcode.split(";");
      if (parts.length !== 3) return;

      const [scannedCaseNumber] = parts;
      const isCurrentCase =
        caseData &&
        cassetteBelongsToOpenCuttingCase(scannedCaseNumber, caseData);

      if (isCurrentCase) {
        setBarcodeInput(barcode);
        const [, specimenLetter, blockNumber] = parts;
        const targetSpecimen = (caseData.specimens || []).find(
          (specimen: any) => specimen.number === specimenLetter
        );
        const targetBlock = targetSpecimen?.blocks?.find(
          (b: any) => b.number === blockNumber
        );
        const foundBlock = targetBlock
          ? { ...targetBlock, specimenNumber: targetSpecimen.number }
          : null;

        if (!foundBlock) {
          notifications.show({
            title: "Error",
            message: `Block ${specimenLetter}-${blockNumber} not found in this case`,
            color: "red",
          });
          setBarcodeInput("");
          return;
        }

        // Check for received-external slides on this block
        const blockSlidesForScanner = (caseData?.slides || []).filter(
          (s: any) => s.block?.id === foundBlock.id
        );
        const hasReceivedExternalScanner =
          blockSlidesForScanner.length > 0 &&
          blockSlidesForScanner.every(isReceivedExternalSlide);
        if (hasReceivedExternalScanner) {
          setReceivedExternalInfo({
            itemLabel: `Block ${specimenLetter}-${blockNumber}`,
            caseNumber: displayCaseNumber,
          });
          setBarcodeInput("");
          return;
        }

        try {
          notifications.show({
            title: "Printing",
            message: "Sending slides to printer...",
            color: "blue",
          });

          const result = await processCassetteScanMutation.mutateAsync({
            caseId: caseId!,
            blockId: foundBlock.id,
          });
          recordCuttingCompletion(result);

          const blockKey = `${specimenLetter}-${blockNumber}`;

          if (result.autoAdvanced) {
            refetch();
          }

          if (result.printJob?.count > 0) {
            if (result.slides && result.slides.length > 0 && caseData) {
              const labelData = convertToSlideLabelData(
                result.slides,
                caseData,
                caseData.specimens || []
              );
              await printSlideLabelsOrThrow(labelData);
              notifications.show({
                title: "Success",
                message: `Cassette ${blockKey} scanned. ${result.printJob.count} slide label(s) sent to printer.`,
                color: "green",
              });
            } else {
              notifications.show({
                title: "Success",
                message: `Cassette ${blockKey} scanned. ${result.printJob.count} slide(s) printed.`,
                color: "green",
              });
            }
          } else {
            notifications.show({
              title: "Info",
              message: `Cassette ${blockKey} scanned. Slides already printed.`,
              color: "blue",
            });
          }

          setScannedBlockKey(blockKey);
          setScrollTrigger((prev) => prev + 1);
          setBarcodeInput("");
          refetch();
        } catch (err: any) {
          notifications.show({
            title: "Error",
            message: err.message || "Failed to scan barcode",
            color: "red",
          });
          setBarcodeInput("");
        }
      } else {
        setNextCaseScanInput(barcode);
        setNextCaseScanError(null);
        if (!canScanNextCase) {
          setNextCaseScanError(
            "Finish the current case before scanning the next case"
          );
          setNextCaseScanInput("");
          return;
        }
        try {
          const caseDataRes = await openCaseByBarcodeMutation.mutateAsync({
            barcode,
            intent: "cutting",
          });
          const statusName = caseDataRes.currentStatus?.name || "";
          if (statusName !== "Pending Cutting" && statusName !== "Cutting") {
            setNextCaseScanError(
              `Case ${scannedCaseNumber} is not in the cutting queue (status: ${
                statusName || "Unknown"
              })`
            );
            setNextCaseScanInput("");
            return;
          }
          const [, specLetter, blockNum] = parts;
          navigate(
            `/cutting/${caseDataRes.id}?block=${specLetter}-${blockNum}`
          );
          setNextCaseScanInput("");
          setNextCaseScanError(null);
        } catch (err: any) {
          setNextCaseScanError(err.message || "Failed to open case");
          setNextCaseScanInput("");
        }
      }
    },
    [
      caseData,
      caseId,
      processCassetteScanMutation,
      openCaseByBarcodeMutation,
      navigate,
      refetch,
      recordCuttingCompletion,
      canScanNextCase,
      displayCaseNumber,
    ]
  );

  const handleAutomaticBarcodeScan = useCallback(
    async (barcode: string) => {
      if (
        scannerHasTransientState ||
        automaticScannerActionInFlightRef.current
      ) {
        if (activeScannerBarcodeRef.current !== barcode) {
          deferredScannerBarcodeRef.current ??= barcode;
        }
        return;
      }

      automaticScannerActionInFlightRef.current = true;
      activeScannerBarcodeRef.current = barcode;
      setAutomaticScannerActionInFlight(true);
      try {
        await processAutomaticBarcodeScan(barcode);
      } finally {
        automaticScannerActionInFlightRef.current = false;
        activeScannerBarcodeRef.current = null;
        setAutomaticScannerActionInFlight(false);
      }
    },
    [processAutomaticBarcodeScan, scannerHasTransientState]
  );

  useEffect(() => {
    if (
      scannerHasBlockingState ||
      scannerHasTransientState ||
      isLockedByOther ||
      automaticScannerActionInFlightRef.current
    ) {
      return;
    }

    const deferredBarcode = deferredScannerBarcodeRef.current;
    if (!deferredBarcode) return;

    deferredScannerBarcodeRef.current = null;
    void handleAutomaticBarcodeScan(deferredBarcode);
  }, [
    handleAutomaticBarcodeScan,
    automaticScannerActionInFlight,
    isLockedByOther,
    scannerHasBlockingState,
    scannerHasTransientState,
  ]);

  useBarcodeScanner(handleAutomaticBarcodeScan, {
    enabled: Boolean(
      caseId && caseData && !isLockedByOther && !scannerHasBlockingState
    ),
    ownerKey: `cutting-detail:${caseId ?? "loading"}`,
    isCompleteBarcode: isCompleteCassetteBarcode,
  });

  // Handle manual block print via click
  const handleManualBlockPrint = async (
    specimenNumber: string,
    blockNumber: string,
    blockId: string
  ) => {
    // Skip if case is blocked
    if (caseData?.isBlocked) return;

    const blockKey = `${specimenNumber}-${blockNumber}`;

    // Check if already printed
    const blockSlides = (caseData?.slides || []).filter(
      (s: any) => s.block?.id === blockId
    );
    const allPrinted =
      blockSlides.length > 0 &&
      blockSlides.every((s: any) => s.printedAt || s.isCompleted);
    if (allPrinted) {
      notifications.show({
        title: "Already Printed",
        message: `Block ${blockKey} slides have already been printed`,
        color: "blue",
      });
      return;
    }

    try {
      notifications.show({
        title: "Printing",
        message: `Printing slides for block ${blockKey}...`,
        color: "blue",
      });

      const result = await processCassetteScanMutation.mutateAsync({
        caseId: caseId!,
        blockId,
      });
      recordCuttingCompletion(result);

      // Check if auto-advanced
      if (result.autoAdvanced) {
        refetch();
      }

      if (result.printJob?.count > 0) {
        if (result.slides && result.slides.length > 0 && caseData) {
          const labelData = convertToSlideLabelData(
            result.slides,
            caseData,
            caseData.specimens || []
          );
          await printSlideLabelsOrThrow(labelData);

          notifications.show({
            title: "Success",
            message: `Block ${blockKey}: ${result.printJob.count} slide label(s) sent to printer.`,
            color: "green",
          });
        } else {
          notifications.show({
            title: "Success",
            message: `Block ${blockKey}: ${result.printJob.count} slide(s) printed`,
            color: "green",
          });
        }
      } else {
        notifications.show({
          title: "Info",
          message: `Block ${blockKey} slides already printed`,
          color: "blue",
        });
      }

      setScannedBlockKey(blockKey);
      setScrollTrigger((prev) => prev + 1); // Force scroll even if same block
      refetch();
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err.message || "Failed to print slides",
        color: "red",
      });
    }
  };

  // Handle slide selection
  const handleToggleSlide = (slideId: string) => {
    setSelectedSlides((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(slideId)) {
        newSet.delete(slideId);
      } else {
        newSet.add(slideId);
      }
      return newSet;
    });
  };

  // Handle select all for a specimen
  const handleSelectAllSpecimen = (slideIds: string[]) => {
    setSelectedSlides((prev) => {
      const newSet = new Set(prev);
      const allSelected = slideIds.every((id) => newSet.has(id));
      if (allSelected) {
        slideIds.forEach((id) => newSet.delete(id));
      } else {
        slideIds.forEach((id) => newSet.add(id));
      }
      return newSet;
    });
  };

  // Check if selected slides include any printed slides (for reprint button)
  const selectedPrintedSlides = useMemo(() => {
    if (!caseData?.slides) return [];
    return caseData.slides.filter(
      (s: any) => selectedSlides.has(s.id) && (s.printedAt || s.isCompleted)
    );
  }, [caseData?.slides, selectedSlides]);

  // Handle print selected slides (reprint)
  const handlePrintSelected = async () => {
    if (selectedPrintedSlides.length === 0 || !caseId) return;

    setIsPrintingSelected(true);

    try {
      // Separate glass slides from sticker slides
      const glassSlides = selectedPrintedSlides.filter(
        (s: any) => !s.isCompleted
      );
      const stickerSlides = selectedPrintedSlides.filter(
        (s: any) => s.isCompleted
      );

      if (glassSlides.length > 0) {
        const slideIds = glassSlides.map((s: any) => s.id);
        const result = await reprintSlidesMutation.mutateAsync(slideIds);

        // Print selected slides using the printer service
        if (caseData) {
          const labelData = convertToSlideLabelData(
            glassSlides,
            caseData,
            caseData.specimens || []
          );
          await printSlideLabelsOrThrow(labelData);

          notifications.show({
            title: "Success",
            message: `${result.count} slide label(s) reprinted on SlideMate.`,
            color: "green",
          });
        }
      }

      if (stickerSlides.length > 0) {
        // For stickers, we advise reprinting them individually via the row buttons
        // to ensure workstation/device context is handled correctly, or we could
        // implement batch sticker printing here if needed.
        // Given the complexity of workstation/device state in this page,
        // we'll show a notification.
        notifications.show({
          title: "Sticker Reprint",
          message: `${stickerSlides.length} received external slide(s) selected. Please use the individual 'Reprint Sticker' buttons for these items.`,
          color: "blue",
        });
      }

      setSelectedSlides(new Set());
      refetch();
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err.message || "Failed to reprint slides",
        color: "red",
      });
    } finally {
      setIsPrintingSelected(false);
    }
  };

  // Handle single slide reprint (short cut by clicking slide number)
  const handleSingleSlideReprint = useCallback(
    async (slide: any) => {
      if (
        !caseId ||
        !caseData ||
        reprintSlidesMutation.isPending ||
        isPrintingSelected
      )
        return;

      // Guard: Case blocked
      if (caseData.isBlocked) {
        notifications.show({
          title: "Reprint Blocked",
          message:
            "This case has a blocking error. Please resolve it before reprinting.",
          color: "orange",
        });
        return;
      }

      setIsPrintingSelected(true);
      try {
        if (slide.isCompleted) {
          // Sticker Slide: Route to existing button (direct user)
          notifications.show({
            title: "Sticker Reprint",
            message:
              "Please use the 'Reprint Sticker' button next to this slide for received external items.",
            color: "blue",
          });
        } else {
          // Glass Slide: Call mutation and printer service
          const label = convertToSlideLabelData(
            [slide],
            caseData,
            caseData.specimens || []
          )[0];
          await printSelectedSlide({
            caseId,
            slideId: slide.id,
            source: "cutting",
            label,
          });

          // Print selected slides using the printer service
          if (caseData) {
            notifications.show({
              title: "Success",
              message: "Selected slide label printed on SlideMate.",
              color: "green",
            });
          }
          refetch();
        }
      } catch (err: any) {
        notifications.show({
          title: "Error",
          message: selectedPrintError(err),
          color: "red",
        });
      } finally {
        setIsPrintingSelected(false);
      }
    },
    [caseId, caseData, reprintSlidesMutation, isPrintingSelected, refetch]
  );

  // Handle cancel/back
  const handleCancel = async () => {
    closeCancelModal();
    if (caseId) {
      await releaseLockMutation.mutateAsync(caseId);
    }
    navigate("/cutting");
  };

  // Scroll to errors section handler
  const scrollToErrors = () => {
    const scrollContainer = document.querySelector(".left-sidebar-scroll");
    const errorsSection = document.querySelector("[data-errors-card]");
    if (scrollContainer && errorsSection) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const errorRect = errorsSection.getBoundingClientRect();
      const scrollTop =
        errorRect.top - containerRect.top + scrollContainer.scrollTop;
      scrollContainer.scrollTo({
        top: scrollTop,
        behavior: "smooth",
      });
    }
  };

  // Loading state
  if (caseLoading) {
    return (
      <Box
        style={{
          height: "calc(100vh - 76px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Group>
          <Loader />
          <Text>Loading case...</Text>
        </Group>
      </Box>
    );
  }

  // Error state
  if (caseError || !caseData) {
    return (
      <Box p="xl">
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
          Failed to load case data. Please try again.
        </Alert>
        <Button
          mt="md"
          onClick={() => navigate("/cutting")}
          leftSection={<IconArrowLeft size={16} />}
        >
          Back to Queue
        </Button>
      </Box>
    );
  }

  // Locked by another user state
  if (isLockedByOther) {
    return (
      <Box p="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="orange"
          title="Case Locked"
        >
          This case is currently being worked on by{" "}
          <strong>{lockData?.lockedByName || "another user"}</strong>. Please
          try again later or choose a different case from the queue.
        </Alert>
        <Group mt="md">
          <Button
            onClick={() => navigate("/cutting")}
            leftSection={<IconArrowLeft size={16} />}
          >
            Back to Queue
          </Button>
          {canForceRelease && (
            <Button
              color="red"
              variant="light"
              leftSection={<IconLockOpen size={16} />}
              onClick={handleForceUnlock}
              loading={forceReleaseMutation.isPending}
            >
              Force Unlock
            </Button>
          )}
        </Group>
      </Box>
    );
  }

  // Group slides by specimen, then by block (all sorted alphabetically)
  const slidesBySpecimenAndBlock = [...(caseData.specimens || [])]
    .sort((a: any, b: any) => (a.number || "").localeCompare(b.number || ""))
    .map((specimen: any) => {
      const blocks = (specimen.blocks || [])
        .sort((a: any, b: any) =>
          (a.number || "").localeCompare(b.number || "")
        )
        .map((block: any) => {
          // Find slides for this specific block
          const blockSlides = (caseData.slides || [])
            .filter((slide: any) => slide.block?.id === block.id)
            .sort((a: any, b: any) =>
              (a.number || "").localeCompare(b.number || "")
            );

          return {
            blockNumber: block.number,
            blockId: block.id,
            slides: blockSlides,
          };
        });

      return {
        specimen,
        blocks,
      };
    });

  const totalSlides = caseData.slides?.length || 0;
  const printedSlides =
    caseData.slides?.filter((s: any) => s.printedAt || s.isCompleted).length ||
    0;
  const totalBlocks = (caseData.specimens || []).reduce(
    (acc: number, s: any) => acc + (Number(s.blocks?.length) || 0),
    0
  );

  // Calculate cut progress (blocks with all slides printed)
  const processedBlocks = (caseData.specimens || []).reduce(
    (acc: number, specimen: any) => {
      const blocksWithAllSlidesPrinted = (specimen.blocks || []).filter(
        (block: any) => {
          const blockSlides = (caseData.slides || []).filter(
            (s: any) => s.block?.id === block.id
          );
          return (
            blockSlides.length > 0 &&
            blockSlides.every((s: any) => s.printedAt || s.isCompleted)
          );
        }
      );
      return acc + (Number(blocksWithAllSlidesPrinted.length) || 0);
    },
    0
  );

  // Check if a block is the scanned block
  const isScannedBlock = (specimenNumber: string, blockNumber: string) => {
    return scannedBlockKey === `${specimenNumber}-${blockNumber}`;
  };

  // Format printed timestamp as relative time or absolute date
  const formatPrintedTime = (printedAt: string) => {
    const date = dayjs(printedAt);
    const now = dayjs();
    const diffInDays = now.diff(date, "day");

    // If more than 30 days, show absolute date/time
    if (diffInDays >= 30) {
      return date.format("D MMM, YYYY h:mma");
    }

    // Otherwise show relative time
    return date.fromNow();
  };

  return (
    <>
      <style>{`
        .hide-scrollbar {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      <Grid gutter="md" style={{ margin: "0 auto", maxWidth: "2200px" }}>
        {/* LEFT SIDEBAR - Case Summary, Notes, Errors */}
        <Grid.Col span={{ base: 12, lg: 2 }} style={{ minWidth: 300 }}>
          <Box
            style={{
              height: "calc(100vh - 76px)",
              position: "relative",
              borderRight: "1px solid var(--mantine-color-gray-3)",
            }}
          >
            <Box
              className="hide-scrollbar left-sidebar-scroll"
              style={{
                height: "100%",
                overflowY: "auto",
              }}
            >
              <Stack gap="md" p="md">
                {/* Case Summary */}
                <GrossingCaseSummary
                  caseId={caseId}
                  caseNumber={displayCaseNumber}
                  externalCaseNumber={caseData.externalCaseNumber}
                  patient={caseData.patient}
                  procedure={caseData.procedure}
                  currentStatus={caseData.currentStatus}
                  isBlocked={caseData.isBlocked}
                  isRush={caseData.isRush}
                  isHighPriority={caseData.isHighPriority}
                  accessioningType={caseData.accessioningType}
                  pathologist={caseData.pathologist}
                  facility={caseData.facility}
                  specimenCount={caseData.specimens?.length || 0}
                  blocksCount={totalBlocks}
                  slidesCount={totalSlides}
                  hasPreviousCase={hasPreviousCase}
                  hasNextCase={hasNextCase}
                  onPreviousCase={() =>
                    previousCaseId && navigateToCase(previousCaseId)
                  }
                  onNextCase={() => nextCaseId && navigateToCase(nextCaseId)}
                />

                <GrossingRequisitionCard key={caseId} caseId={caseId!} />

                {/* Case Flags */}
                <CaseFlagsSection caseId={caseId || null} />

                {/* Case Notes */}
                <CaseNotesSection caseId={caseId || null} />

                {/* Case Comments */}
                <CaseCommentsSection caseId={caseId || null} />

                {/* Errors */}
                <div ref={errorsRef} data-errors-card>
                  <ErrorsCard caseId={caseId || null} />
                </div>
              </Stack>
            </Box>

            {/* Scroll indicator */}
            {showScrollIndicator && (
              <Box
                onClick={() => {
                  const scrollContainer = document.querySelector(
                    ".left-sidebar-scroll"
                  );
                  if (scrollContainer) {
                    scrollContainer.scrollBy({ top: 150, behavior: "smooth" });
                  }
                }}
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 36,
                  background:
                    "linear-gradient(to bottom, rgba(66, 133, 244, 0), rgba(66, 133, 244, 0.15))",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                  paddingBottom: 6,
                }}
              >
                <Box
                  style={{
                    backgroundColor: "rgba(66, 133, 244, 0.2)",
                    borderRadius: 12,
                    padding: "2px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <IconChevronDown
                    size={14}
                    color="var(--mantine-color-blue-6)"
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Grid.Col>

        {/* CENTER COLUMN - Slides Tables */}
        <Grid.Col span={{ base: 12, lg: "auto" }} style={{ flex: 1 }}>
          <Box
            style={{
              height: "calc(100vh - 76px)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Scrollable content area */}
            <Box style={{ flex: 1, overflowY: "auto" }} p="md">
              {/* Blocked Case Alert */}
              {caseData.isBlocked && (
                <Alert
                  icon={<IconAlertTriangle size={16} />}
                  color="orange"
                  title="Case Blocked"
                  mb="md"
                >
                  <Text>
                    {printedSlides === totalSlides && totalSlides > 0
                      ? "All slides have been printed, but the case cannot advance to staining because it has a blocking error. Please resolve the error to continue."
                      : "This case has a blocking error. You can still scan cassettes and print labels, but auto-advance to staining is blocked until the error is resolved."}
                  </Text>{" "}
                  <Anchor
                    td="underline"
                    onClick={scrollToErrors}
                    style={{ cursor: "pointer" }}
                  >
                    View errors
                  </Anchor>
                </Alert>
              )}

              {receivedExternalInfo && (
                <ReceivedExternalScanInfoPanel
                  itemLabel={receivedExternalInfo.itemLabel}
                  onDismiss={handleDismissReceivedExternalInfo}
                />
              )}

              <Stack gap="md">
                <Group justify="space-between" align="center">
                  <Group gap="md" py="4px">
                    <Text fw={500} size="lg">
                      Cassettes & Slides
                    </Text>
                    <Badge
                      size="sm"
                      color={printedSlides === totalSlides ? "green" : "blue"}
                    >
                      {printedSlides} / {totalSlides} printed
                    </Badge>
                  </Group>
                  {selectedSlides.size > 0 && (
                    <Group gap="sm">
                      <Badge size="sm" color="blue">
                        {selectedSlides.size} selected
                      </Badge>
                      {selectedPrintedSlides.length > 0 && (
                        <Button
                          size="sm"
                          onClick={handlePrintSelected}
                          loading={isPrintingSelected}
                          leftSection={<IconPrinter size={14} />}
                        >
                          Reprint Selected ({selectedPrintedSlides.length})
                        </Button>
                      )}
                    </Group>
                  )}
                </Group>

                <Text size="xs" c="dimmed" fs="italic">
                  Tip: Click a block badge to print its slides manually
                </Text>

                <Grid gutter="md">
                  {slidesBySpecimenAndBlock.map((item: any) => {
                    // Get only printed slides for this specimen for select all functionality
                    const printedSpecimenSlideIds = item.blocks.flatMap(
                      (b: any) =>
                        b.slides
                          .filter((s: any) => s.printedAt || s.isCompleted)
                          .map((s: any) => s.id)
                    );
                    const allSpecimenSelected =
                      printedSpecimenSlideIds.length > 0 &&
                      printedSpecimenSlideIds.every((id: string) =>
                        selectedSlides.has(id)
                      );
                    const someSpecimenSelected = printedSpecimenSlideIds.some(
                      (id: string) => selectedSlides.has(id)
                    );

                    return (
                      <Grid.Col
                        key={item.specimen.id}
                        span={{ base: 12, md: 6 }}
                      >
                        <Paper p="md" withBorder radius="md" h="100%">
                          <Stack gap="sm">
                            <Group
                              justify="space-between"
                              align="center"
                              wrap="nowrap"
                            >
                              {item.specimen.insufficientTissue ? (
                                <InsufficientTissueConfirmationModal
                                  caseId={caseId!}
                                  specimen={item.specimen}
                                  sourceName={
                                    item.specimen.source?.name || "Unknown"
                                  }
                                  disabled={isLockedByOther}
                                />
                              ) : (
                                <Text fw={600} size="md">
                                  Specimen {item.specimen.number} -{" "}
                                  {item.specimen.source?.name || "Unknown"}
                                </Text>
                              )}
                              {["Pending Cutting", "Cutting"].includes(
                                caseData.currentStatus?.name
                              ) && (
                                <Tooltip
                                  label="Edit grossing description"
                                  withArrow
                                >
                                  <ActionIcon
                                    variant="subtle"
                                    size="sm"
                                    onClick={() =>
                                      handleOpenGrossEditor(item.specimen)
                                    }
                                  >
                                    <IconEdit size={14} />
                                  </ActionIcon>
                                </Tooltip>
                              )}
                            </Group>

                            <ScrollArea.Autosize
                              mah={500}
                              offsetScrollbars
                              type="auto"
                              data-testid={`cutting-slides-${item.specimen.id}`}
                            >
                              <Table highlightOnHover striped stickyHeader>
                                <Table.Thead>
                                  <Table.Tr>
                                    <Table.Th style={{ width: "50px" }}>
                                      <Checkbox
                                        size="sm"
                                        checked={allSpecimenSelected}
                                        indeterminate={
                                          someSpecimenSelected &&
                                          !allSpecimenSelected
                                        }
                                        onChange={() =>
                                          handleSelectAllSpecimen(
                                            printedSpecimenSlideIds
                                          )
                                        }
                                        disabled={
                                          printedSpecimenSlideIds.length === 0
                                        }
                                      />
                                    </Table.Th>
                                    <Table.Th
                                      style={{
                                        width: "100px",
                                        fontSize: "14px",
                                        fontWeight: 600,
                                      }}
                                    >
                                      Block
                                    </Table.Th>
                                    <Table.Th
                                      style={{
                                        width: "80px",
                                        fontSize: "14px",
                                        fontWeight: 600,
                                      }}
                                    >
                                      Slide
                                    </Table.Th>
                                    <Table.Th
                                      style={{
                                        fontSize: "14px",
                                        fontWeight: 600,
                                      }}
                                    >
                                      Stain
                                    </Table.Th>
                                    <Table.Th
                                      style={{
                                        width: "150px",
                                        fontSize: "14px",
                                        fontWeight: 600,
                                      }}
                                    >
                                      Printed
                                    </Table.Th>
                                  </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                  {item.blocks.map((blockData: any) => {
                                    const blockKey = `${item.specimen.number}-${blockData.blockNumber}`;
                                    const isBlockHighlighted = isScannedBlock(
                                      item.specimen.number,
                                      blockData.blockNumber
                                    );
                                    const blockAllPrinted =
                                      blockData.slides.length > 0 &&
                                      blockData.slides.every(
                                        (s: any) => s.printedAt || s.isCompleted
                                      );
                                    const canPrint =
                                      !blockAllPrinted && !caseData.isBlocked;

                                    if (blockData.slides.length === 0) {
                                      return (
                                        <Table.Tr
                                          key={blockKey}
                                          ref={
                                            isBlockHighlighted
                                              ? highlightedBlockRef
                                              : null
                                          }
                                          style={{
                                            backgroundColor: isBlockHighlighted
                                              ? "var(--mantine-color-blue-0)"
                                              : undefined,
                                            borderLeft: isBlockHighlighted
                                              ? "3px solid var(--mantine-color-blue-5)"
                                              : undefined,
                                          }}
                                        >
                                          <Table.Td />
                                          <Table.Td>
                                            <Group gap="xs">
                                              <Badge
                                                variant="filled"
                                                color="cyan"
                                                size="sm"
                                              >
                                                {blockKey}
                                              </Badge>
                                              {isBlockHighlighted && (
                                                <Badge
                                                  color="blue"
                                                  size="xs"
                                                  leftSection={
                                                    <IconCheck size={10} />
                                                  }
                                                >
                                                  Scanned
                                                </Badge>
                                              )}
                                            </Group>
                                          </Table.Td>
                                          <Table.Td colSpan={3}>
                                            <Text size="sm" c="dimmed">
                                              No slides
                                            </Text>
                                          </Table.Td>
                                        </Table.Tr>
                                      );
                                    }

                                    return blockData.slides.map(
                                      (slide: any, slideIndex: number) => {
                                        const isPrinted = !!slide.printedAt;

                                        const rowBgColor = isBlockHighlighted
                                          ? "var(--mantine-color-blue-0)"
                                          : undefined;

                                        return (
                                          <Table.Tr
                                            key={slide.id}
                                            ref={
                                              isBlockHighlighted &&
                                              slideIndex === 0
                                                ? highlightedBlockRef
                                                : null
                                            }
                                            style={{
                                              backgroundColor: rowBgColor,
                                              borderLeft: isBlockHighlighted
                                                ? "3px solid var(--mantine-color-blue-5)"
                                                : undefined,
                                            }}
                                          >
                                            <Table.Td>
                                              {slide.printedAt ||
                                              slide.isCompleted ? (
                                                <Checkbox
                                                  size="sm"
                                                  checked={selectedSlides.has(
                                                    slide.id
                                                  )}
                                                  onChange={() =>
                                                    handleToggleSlide(slide.id)
                                                  }
                                                />
                                              ) : null}
                                            </Table.Td>
                                            <Table.Td>
                                              {slideIndex === 0 && (
                                                <Group gap="xs">
                                                  <Tooltip
                                                    label="Click to print slides"
                                                    disabled={
                                                      blockAllPrinted ||
                                                      caseData.isBlocked
                                                    }
                                                    withArrow
                                                    position="top"
                                                  >
                                                    <Badge
                                                      variant="filled"
                                                      color={
                                                        blockAllPrinted
                                                          ? "green"
                                                          : "cyan"
                                                      }
                                                      size="sm"
                                                      leftSection={
                                                        blockAllPrinted ? (
                                                          <IconCheck
                                                            size={10}
                                                          />
                                                        ) : null
                                                      }
                                                      style={{
                                                        cursor: canPrint
                                                          ? "pointer"
                                                          : "default",
                                                      }}
                                                      onClick={() =>
                                                        canPrint &&
                                                        handleManualBlockPrint(
                                                          item.specimen.number,
                                                          blockData.blockNumber,
                                                          blockData.blockId
                                                        )
                                                      }
                                                    >
                                                      {blockKey}
                                                    </Badge>
                                                  </Tooltip>
                                                  {isBlockHighlighted && (
                                                    <Badge
                                                      color="blue"
                                                      size="xs"
                                                      leftSection={
                                                        <IconCheck size={10} />
                                                      }
                                                    >
                                                      Scanned
                                                    </Badge>
                                                  )}
                                                </Group>
                                              )}
                                            </Table.Td>
                                            <Table.Td>
                                              <Tooltip
                                                label={
                                                  caseData.isBlocked
                                                    ? "Case is blocked"
                                                    : slide.printedAt ||
                                                      slide.isCompleted
                                                    ? "Click to reprint this slide"
                                                    : "Click to print this slide"
                                                }
                                                withArrow
                                                position="top"
                                              >
                                                <Badge
                                                  size="sm"
                                                  variant={
                                                    isPrinted
                                                      ? "filled"
                                                      : "light"
                                                  }
                                                  color={
                                                    isPrinted ? "green" : "gray"
                                                  }
                                                  leftSection={
                                                    isPrinted ? (
                                                      <IconCheck size={12} />
                                                    ) : null
                                                  }
                                                  styles={{
                                                    label: {
                                                      color: !isPrinted
                                                        ? "var(--mantine-color-black)"
                                                        : undefined,
                                                    },
                                                  }}
                                                  style={{
                                                    cursor: !caseData.isBlocked
                                                      ? "pointer"
                                                      : "default",
                                                  }}
                                                  onClick={() =>
                                                    handleSingleSlideReprint(
                                                      slide
                                                    )
                                                  }
                                                >
                                                  {getPhysicalSlideNumber(
                                                    slide
                                                  )}
                                                </Badge>
                                              </Tooltip>
                                            </Table.Td>
                                            <Table.Td>
                                              <Group gap="xs" wrap="nowrap">
                                                <Text size="sm">
                                                  {slide.stain?.code ||
                                                    "No stain"}
                                                </Text>
                                                {isReceivedExternalSlide(
                                                  slide
                                                ) && (
                                                  <>
                                                    <ReceivedExternalBadge
                                                      compact
                                                    />
                                                    <PrintStickerButton
                                                      caseId={caseId!}
                                                      itemId={slide.id}
                                                      itemType="slide"
                                                      isCompleted
                                                    />
                                                  </>
                                                )}
                                                <DeleteStainButton
                                                  slide={slide}
                                                  caseId={caseId!}
                                                  caseNumber={
                                                    caseData.caseNumber
                                                  }
                                                  slideLabel={`${blockKey}-${slide.number}`}
                                                  disabled={caseData.isBlocked}
                                                />
                                              </Group>
                                            </Table.Td>
                                            <Table.Td>
                                              {slide.printedAt ? (
                                                <Text size="xs">
                                                  {formatPrintedTime(
                                                    slide.printedAt
                                                  )}
                                                </Text>
                                              ) : (
                                                <Text size="xs" c="dimmed">
                                                  -
                                                </Text>
                                              )}
                                            </Table.Td>
                                          </Table.Tr>
                                        );
                                      }
                                    );
                                  })}
                                </Table.Tbody>
                              </Table>
                            </ScrollArea.Autosize>
                          </Stack>
                        </Paper>
                      </Grid.Col>
                    );
                  })}
                </Grid>
              </Stack>

              {/* Scan Next Case or All Cutting Complete - shown when all blocks printed */}
              {printedSlides === totalSlides && totalSlides > 0 && (
                <Box style={{ maxWidth: 520 }}>
                  <Paper
                    p="md"
                    withBorder
                    mt="md"
                    style={{
                      borderLeft: "4px solid var(--mantine-color-green-5)",
                      backgroundColor: "var(--mantine-color-green-0)",
                    }}
                  >
                    <Stack gap="sm">
                      {!isCuttingFinalized ? (
                        <>
                          <Text fw={600} size="md">
                            Cutting Completion Required
                          </Text>
                          <Alert
                            icon={<IconAlertTriangle size={18} />}
                            color="yellow"
                            title="Waiting for completion"
                            p="sm"
                            style={{ width: "fit-content" }}
                            styles={{ message: { fontSize: 13 } }}
                          >
                            All slide labels are printed, but the case has not
                            moved out of cutting. Complete cutting before
                            scanning the next case.
                          </Alert>
                        </>
                      ) : hasOtherCasesInQueue ? (
                        <>
                          <Text fw={600} size="md">
                            Scan Next Case
                          </Text>
                          <Alert
                            icon={<IconCheck size={18} />}
                            color="green"
                            variant="filled"
                            title="Ready for next case"
                            p="sm"
                            style={{ width: "fit-content" }}
                            styles={{ message: { fontSize: 13 } }}
                          >
                            Cutting is complete and the case moved to{" "}
                            {cuttingDestinationName}. Scan the next block to
                            move to that case.
                          </Alert>
                          {showLegacyScannerControls && (
                            <TextInput
                              size="sm"
                              placeholder="Scan block barcode..."
                              value={nextCaseScanInput}
                              onChange={(e) =>
                                setNextCaseScanInput(e.target.value)
                              }
                              onKeyDown={(e) =>
                                e.key === "Enter" && handleNextCaseScan()
                              }
                              autoFocus
                            />
                          )}
                          {nextCaseScanError && (
                            <Text size="xs" c="red">
                              {nextCaseScanError}
                            </Text>
                          )}
                        </>
                      ) : (
                        <>
                          <Text fw={600} size="md">
                            All Cutting Complete
                          </Text>
                          <Alert
                            icon={<IconCheck size={18} />}
                            color="green"
                            variant="filled"
                            title="No more cases"
                            p="sm"
                            style={{ width: "fit-content" }}
                            styles={{ message: { fontSize: 13 } }}
                          >
                            Cutting is complete and the case moved to{" "}
                            {cuttingDestinationName}. There are no more cases in
                            the cutting queue.
                          </Alert>
                        </>
                      )}
                    </Stack>
                  </Paper>
                </Box>
              )}
            </Box>

            {/* FIXED Footer - Back to Queue + Complete Cutting */}
            <Box
              style={{
                borderTop: "1px solid var(--mantine-color-gray-3)",
                padding: "var(--mantine-spacing-md)",
                backgroundColor: "white",
              }}
            >
              <Group justify="space-between">
                <Button
                  variant="outline"
                  leftSection={<IconArrowLeft size={16} />}
                  onClick={() => {
                    // If blocked, go directly to queue (no lock to release)
                    if (caseData.isBlocked) {
                      navigate("/cutting");
                    } else if (
                      printedSlides === totalSlides &&
                      totalSlides > 0
                    ) {
                      // Fully cut - no confirmation needed, just release lock and go
                      handleCancel();
                    } else {
                      openCancelModal();
                    }
                  }}
                >
                  Back to Queue
                </Button>
                {!caseData.isBlocked &&
                  printedSlides === totalSlides &&
                  totalSlides > 0 &&
                  ACTIVE_CUTTING_STATUSES.has(caseData.currentStatus?.name) && (
                    <Button
                      color="green"
                      leftSection={<IconCheck size={16} />}
                      onClick={handleCompleteCutting}
                      loading={completeCuttingMutation.isPending}
                    >
                      Complete Cutting
                    </Button>
                  )}
                {/* Show Next button when progress is 100% and there's a next case */}
                {printedSlides === totalSlides &&
                  totalSlides > 0 &&
                  isCuttingFinalized &&
                  hasNextCase && (
                    <Button
                      color="blue"
                      variant={caseData.isBlocked ? "filled" : "outline"}
                      leftSection={<IconArrowRight size={16} />}
                      onClick={() => {
                        if (nextCaseId) {
                          navigateToCase(nextCaseId);
                        }
                      }}
                    >
                      Next
                    </Button>
                  )}
              </Group>
            </Box>
          </Box>
        </Grid.Col>

        {/* RIGHT SIDEBAR - Printer + Scanner + Progress */}
        <Grid.Col span={{ base: 12, lg: 2 }} style={{ minWidth: 320 }}>
          <Box
            style={{
              height: "calc(100vh - 76px)",
              overflowY: "auto",
              borderLeft: "1px solid var(--mantine-color-gray-3)",
            }}
            className="hide-scrollbar"
          >
            <Stack gap="md" p="md">
              {/* Printer Status */}
              <SlidePrinterStatusPanel />

              {/* Sticker Printer Status */}
              <StickerPrinterStatusPanel />

              {/* Barcode Scanner */}
              <Paper p="md" withBorder>
                <Stack gap="xs">
                  <Group gap="xs">
                    <IconBarcode size={16} />
                    <Text fw={500} size="sm">
                      Scan Cassette
                    </Text>
                  </Group>
                  {showLegacyScannerControls && (
                    <>
                      <TextInput
                        size="sm"
                        placeholder="Scan cassette barcode..."
                        value={barcodeInput}
                        onChange={(e) => setBarcodeInput(e.target.value)}
                        onKeyDown={(e) =>
                          e.key === "Enter" && handleBarcodeSubmit()
                        }
                        autoFocus
                      />
                      <Button
                        size="sm"
                        onClick={handleBarcodeSubmit}
                        disabled={!barcodeInput.trim()}
                        loading={processCassetteScanMutation.isPending}
                        fullWidth
                      >
                        Scan & Print
                      </Button>
                    </>
                  )}
                  <Text size="xs" c="dimmed">
                    Scanning a cassette auto-prints its slide labels or you can
                    click on the block to print it
                  </Text>
                </Stack>
              </Paper>

              {/* Cut Progress */}
              <Paper p="md" withBorder>
                <Stack gap="sm">
                  <Text fw={500} size="sm">
                    Cut Progress
                  </Text>
                  <Progress
                    value={
                      totalBlocks > 0
                        ? (processedBlocks / totalBlocks) * 100
                        : 0
                    }
                    size="lg"
                    color={processedBlocks === totalBlocks ? "green" : "blue"}
                  />
                  <Text size="sm" ta="center" c="dimmed">
                    {processedBlocks} of {totalBlocks} blocks cut
                  </Text>
                </Stack>
              </Paper>

              {/* Print Progress */}
              <Paper p="md" withBorder>
                <Stack gap="sm">
                  <Text fw={500} size="sm">
                    Print Progress
                  </Text>
                  <Progress
                    value={
                      totalSlides > 0 ? (printedSlides / totalSlides) * 100 : 0
                    }
                    size="lg"
                    color={printedSlides === totalSlides ? "green" : "blue"}
                  />
                  <Text size="sm" ta="center" c="dimmed">
                    {printedSlides} of {totalSlides} slides printed
                  </Text>
                  {/* Show completion message when all slides are printed */}
                  {printedSlides === totalSlides && totalSlides > 0 && (
                    <Alert
                      icon={
                        isCuttingFinalized ? (
                          <IconCheck size={14} />
                        ) : (
                          <IconAlertTriangle size={14} />
                        )
                      }
                      color={isCuttingFinalized ? "green" : "yellow"}
                      p="xs"
                      styles={{ message: { fontSize: 12 } }}
                    >
                      <Text size="xs">
                        All slides printed for{" "}
                        <strong>{displayCaseNumber}</strong>
                      </Text>
                      {isCuttingFinalized ? (
                        <Text size="xs" c="dimmed">
                          New Status: {cuttingDestinationName}
                        </Text>
                      ) : (
                        <Text size="xs" c="dimmed">
                          Cutting completion has not yet been confirmed.
                        </Text>
                      )}
                    </Alert>
                  )}
                  {lockData && (
                    <Group justify="center" gap="xs">
                      <Badge size="xs" color="green" variant="light">
                        Locked by you
                      </Badge>
                    </Group>
                  )}
                </Stack>
              </Paper>
            </Stack>
          </Box>
        </Grid.Col>
      </Grid>

      {/* Cancel Confirmation Modal */}
      <Modal
        opened={cancelModalOpened}
        onClose={closeCancelModal}
        title="Leave Cutting?"
        centered
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to leave cutting for case {displayCaseNumber}?
            The case lock will be released.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="outline" onClick={closeCancelModal}>
              Stay
            </Button>
            <Button
              color="red"
              onClick={handleCancel}
              loading={releaseLockMutation.isPending}
            >
              Leave
            </Button>
          </Group>
        </Stack>
      </Modal>

      {editingSpecimen && (
        <GrossDescriptionEditorModal
          opened={grossEditorOpened}
          onClose={handleCloseGrossEditor}
          caseId={caseId!}
          specimen={editingSpecimen}
          onSaveSuccess={() => refetch()}
        />
      )}
    </>
  );
}
